/**
 * Shared Feature Registry — the SINGLE source of truth for the Feature Hub
 * AND for feature route wiring.
 *
 * To add a new module: add ONE entry here.
 *   - It appears in the Feature Hub automatically (role-aware).
 *   - If you set `autoRoute: true` and provide `component`, App.js mounts its
 *     route automatically too — no need to touch App.js or a second list.
 *
 * Existing modules keep their manual routes in App.js and are listed here for
 * hub display only (autoRoute omitted). New modules should use autoRoute.
 */
import { lazy } from 'react';
import {
  Shield, ShieldCheck, ShieldAlert, FileSignature, Target, DollarSign, FileCode, Smartphone,
  FileSearch, FileCheck, History, Zap, BarChart3, Bell, ClipboardList, Users,
  Mic, Brain, Building2, Calendar, Newspaper, Globe, Cpu, Activity, Network,
  ClipboardCheck, Gauge, CalendarClock, UsersRound, BadgeCheck, ListChecks, Receipt, Plug, Package, Waypoints, Route,
} from 'lucide-react';

// Auto-routed pages (single-source route + hub card)
const SSVIDashboard = lazy(() => import('../pages/ssvi/SSVIDashboard'));
const SSVIBenchmarkingPage = lazy(() => import('../pages/ssvi/SSVIBenchmarkingPage'));
const SurveyReadinessPage = lazy(() => import('../pages/SurveyReadinessPage'));
const NetworkOversight = lazy(() => import('../pages/NetworkOversight'));
const VerificationSequencePage = lazy(() => import('../pages/VerificationSequencePage'));
const ReadinessCommandCenter = lazy(() => import('../pages/ReadinessCommandCenter'));
const SFPSimulatorPage = lazy(() => import('../pages/SFPSimulatorPage'));
const HopeCockpitPage = lazy(() => import('../pages/HopeCockpitPage'));
const TeamDataPage = lazy(() => import('../pages/TeamDataPage'));
const ComplianceAutopilotPage = lazy(() => import('../pages/ComplianceAutopilotPage'));
const ComplianceWorkQueuePage = lazy(() => import('../pages/ComplianceWorkQueuePage'));
const BillReadyBoardPage = lazy(() => import('../pages/BillReadyBoardPage'));
const EmrConnectionPage = lazy(() => import('../pages/EmrConnectionPage'));
const ModuleProvisioningPage = lazy(() => import('../pages/ModuleProvisioningPage'));
const CredentialTrackerPage = lazy(() => import('../pages/CredentialTrackerPage'));
const MyCredentialsPage = lazy(() => import('../pages/MyCredentialsPage'));
const CensusSyncPage = lazy(() => import('../pages/CensusSyncPage'));
const CareTeamsPage = lazy(() => import('../pages/CareTeamsPage'));
const DeadlineAutoCompletePage = lazy(() => import('../pages/DeadlineAutoCompletePage'));
const DailyDigestPage = lazy(() => import('../pages/DailyDigestPage'));
const NetworkModulesPage = lazy(() => import('../pages/NetworkModulesPage'));
const TPETrackerPage = lazy(() => import('../pages/TPETrackerPage'));
const CAHPSOptimizerPage = lazy(() => import('../pages/CAHPSOptimizerPage'));
const DefenseExecReportPage = lazy(() => import('../pages/DefenseExecReportPage'));
const AuditCenterPage = lazy(() => import('../pages/AuditCenterPage'));
const GoldenThreadPage = lazy(() => import('../pages/GoldenThreadPage'));
const PatientJourneyPage = lazy(() => import('../pages/PatientJourneyPage'));

export const FEATURE_REGISTRY = [
  {
    id: 'patient-journey', label: 'Patient Journey',
    desc: 'One connected record per patient, in care order: referral, election/NOE, CTI, HOPE, visits, IDG, recert & F2F, Golden Thread and the audit-ready case file. Each step shows its status, what to do next, who owns it and a link to the right screen. Nurses get a Pre-Visit Brief in AI Scribe and Voice Note, and see which Golden Thread gaps their saved note closed.',
    icon: Route, path: '/app/patient-journey',
    category: 'compliance', isNew: true, color: 'bg-teal-600',
    tags: ['journey', 'patient', 'timeline', 'referral', 'noe', 'cti', 'hope', 'visit', 'idg', 'recert', 'f2f', 'golden thread', 'audit', 'pre-visit', 'brief', 'next step'],
    roles: ['admin', 'super_admin', 'compliance', 'clinical_manager', 'physician', 'medical_director', 'staff'],
    autoRoute: true, component: PatientJourneyPage,
  },
  {
    id: 'team-data', label: 'Team & Data',
    desc: 'Every user on your hospice in one view, with a data-flow health check that flags any account not attached to the company (data not flowing in).',
    icon: UsersRound, path: '/app/team-data',
    category: 'admin', isNew: true, color: 'bg-blue-700',
    tags: ['team', 'users', 'company code', 'data flow', 'orphan', 'members', 'onboarding', 'admin'],
    roles: ['admin', 'super_admin'],
    autoRoute: true, component: TeamDataPage,
  },
  // 🧭 Readiness
  {
    id: 'readiness', label: 'Readiness Command Center',
    desc: 'One Readiness Index across SFP, audit, HOPE & referral risk — with a ranked "do this next" action loop and a trend line that proves you\'re improving.',
    icon: Gauge, path: '/app/readiness',
    category: 'analytics', isNew: true, color: 'bg-cyan-700',
    tags: ['readiness', 'sfp', 'special focus', 'audit', 'hope', 'referral', 'index', 'action', 'trend', 'command center'],
    roles: ['admin', 'super_admin', 'compliance', 'clinical_manager'],
    autoRoute: true, component: ReadinessCommandCenter,
  },
  {
    id: 'sfp-simulator', label: 'SFP Risk Simulator',
    desc: 'Your Special Focus Program exposure fused the CMS way — claims (SSVI/HCI), CAHPS (double-weighted), condition-level deficiencies & substantiated complaints — with a live what-if simulator.',
    icon: ShieldAlert, path: '/app/sfp-simulator',
    category: 'analytics', isNew: true, color: 'bg-red-700',
    tags: ['sfp', 'special focus', 'risk', 'simulator', 'cahps', 'ssvi', 'hci', 'deficiency', 'complaint', 'what-if'],
    roles: ['admin', 'super_admin', 'compliance', 'clinical_manager'],
    autoRoute: true, component: SFPSimulatorPage,
  },
  {
    id: 'hope-cockpit', label: 'HOPE Cockpit',
    desc: 'Per-patient HOPE timepoint tracker — Admission/HUV1/HUV2/Discharge due windows with overdue & due-soon alerts. On-time rate feeds the Readiness Index.',
    icon: CalendarClock, path: '/app/hope-cockpit',
    category: 'compliance', isNew: true, color: 'bg-cyan-600',
    tags: ['hope', 'timepoint', 'huv1', 'huv2', 'admission', 'discharge', 'due window', 'alerts', 'timeliness'],
    roles: ['admin', 'super_admin', 'compliance', 'clinical_manager', 'physician'],
    autoRoute: true, component: HopeCockpitPage,
  },
  {
    id: 'compliance-autopilot', label: 'Compliance Autopilot',
    desc: 'Auto-generates every recurring CoP deadline per patient — NOE, initial CTI, IDG (15d), aide supervision (14d), recert & F2F by benefit period — with owner assignment and a personal "My Tasks" worklist. Nothing tracked by hand.',
    icon: CalendarClock, path: '/app/compliance-autopilot',
    category: 'compliance', isNew: true, color: 'bg-teal-700',
    tags: ['compliance', 'calendar', 'deadline', 'noe', 'recert', 'f2f', 'idg', 'aide', 'cop', 'tasks', 'autopilot', 'benefit period'],
    roles: ['admin', 'super_admin', 'compliance', 'clinical_manager', 'physician', 'staff'],
    autoRoute: true, component: ComplianceAutopilotPage,
  },
  {
    id: 'compliance-queue', label: 'Compliance Work Queue',
    desc: 'One prioritized inbox for compliance leads — blocking hard-stops (NOE/F2F/CTI), at-risk CoP deadlines, duplicate-narrative flags, and live survey deficiencies. Override gates with a logged reason or jump straight into the right workflow.',
    icon: ListChecks, path: '/app/compliance-queue',
    category: 'compliance', isNew: true, color: 'bg-rose-700',
    tags: ['compliance', 'queue', 'hard stop', 'blocking', 'gate', 'override', 'deficiency', 'duplicate', 'noe', 'f2f', 'cti', 'inbox', 'leads'],
    roles: ['admin', 'super_admin', 'compliance', 'clinical_manager'],
    autoRoute: true, component: ComplianceWorkQueuePage,
  },
  {
    id: 'audit-center', label: 'Audit Center',
    desc: 'One defensible case file per patient — deadlines, hard-stops, CTI/F2F, and defense history assembled from existing data (never fabricated), each cited to its source. A file becomes audit-ready only after a named human signs off, with a reproducibility stamp; sign-off is hard-blocked while a compliance hard-stop is open.',
    icon: ShieldCheck, path: '/app/audit-center',
    category: 'compliance', isNew: true, color: 'bg-teal-700',
    tags: ['audit', 'case file', 'defensible', 'sign-off', 'signoff', 'reproducibility', 'stamp', 'deadlines', 'hard stop', 'cti', 'f2f', 'defense', 'goodwinguard', 'survey', 'packet'],
    roles: ['admin', 'super_admin', 'compliance', 'clinical_manager'],
    autoRoute: true, component: AuditCenterPage,
  },
  {
    id: 'golden-thread', label: 'Golden Thread',
    desc: 'Checks that eligibility, visit notes, HOPE and IDG discussion reference and reinforce each other as one story. Rule checks plus an AI coherence check quote the record where the thread breaks, tie each break to audit exposure, denial risk and CAHPS, and draft sign-off-gated addenda to repair it. Critical breaks block Audit Center sign-off.',
    icon: Waypoints, path: '/app/golden-thread',
    category: 'compliance', isNew: true, color: 'bg-amber-600',
    tags: ['golden thread', 'narrative', 'consistency', 'contradiction', 'eligibility', 'visit', 'hope', 'idg', 'cahps', 'plan of care', 'audit', 'denial', 'sfv', '15 day'],
    roles: ['admin', 'super_admin', 'compliance', 'clinical_manager', 'physician'],
    autoRoute: true, component: GoldenThreadPage,
  },
  {
    id: 'bill-ready', label: 'Bill-Ready Gate',
    desc: 'Finalize claims only when compliance is clean. The "Mark ready to bill" action is hard-blocked by unmet NOE / F2F / CTI milestones, so a claim cannot be submitted while a required certification is missing — override in the Work Queue to proceed.',
    icon: Receipt, path: '/app/bill-ready',
    category: 'compliance', isNew: true, color: 'bg-emerald-700',
    tags: ['billing', 'bill', 'claim', 'finalize', 'gate', 'hard stop', 'noe', 'f2f', 'cti', 'revenue', 'ready'],
    roles: ['admin', 'super_admin', 'compliance', 'clinical_manager', 'physician', 'staff'],
    autoRoute: true, component: BillReadyBoardPage,
  },
  {
    id: 'emr-connection', label: 'Connect Your EMR',
    desc: 'Securely store your EMR (HCHB / MatrixCare / KanTime) API credentials — encrypted at rest — so bidirectional sync can switch on the moment your vendor issues API access. Includes a live endpoint connection test.',
    icon: Plug, path: '/app/emr-connection',
    category: 'organization', isNew: true, color: 'bg-slate-700',
    tags: ['emr', 'integration', 'sync', 'hchb', 'matrixcare', 'kantime', 'api', 'connect', 'credentials'],
    roles: ['admin', 'super_admin'],
    autoRoute: true, component: EmrConnectionPage,
  },
  {
    id: 'module-provisioning', label: 'Module Provisioning',
    desc: 'Super-admin: sell the 5 clinician tools à la carte — flip an agency to à-la-carte and enable only the modules they purchased (the rest are hidden + API-blocked).',
    icon: Package, path: '/app/module-provisioning',
    category: 'organization', isNew: true, color: 'bg-indigo-700',
    tags: ['modules', 'provisioning', 'alacarte', 'packaging', 'sell', 'entitlements', 'super admin'],
    roles: ['super_admin'],
    autoRoute: true, component: ModuleProvisioningPage,
  },
  {
    id: 'credential-tracker', label: 'Credential Tracker',
    desc: 'Staff licensure, competency, health-requirement and background-check expiry tracking — expired & expiring-soon items surface automatically, with a survey-ready roster PDF. Credentialing on the same autopilot as patient deadlines.',
    icon: BadgeCheck, path: '/app/credential-tracker',
    category: 'compliance', isNew: true, color: 'bg-indigo-700',
    tags: ['credential', 'license', 'licensure', 'competency', 'expiry', 'staff', 'personnel', 'background check', 'cpr', 'tb', 'roster'],
    roles: ['admin', 'super_admin', 'compliance', 'clinical_manager'],
    autoRoute: true, component: CredentialTrackerPage,
  },
  {
    id: 'my-credentials', label: 'My Credentials',
    desc: 'Your own licenses, CPR card, TB test and competencies — add them and upload proof directly. Your hospice sees them on the survey roster.',
    icon: BadgeCheck, path: '/app/my-credentials',
    category: 'compliance', isNew: true, color: 'bg-indigo-600',
    tags: ['credential', 'license', 'my', 'self', 'upload', 'proof', 'cpr', 'competency', 'staff'],
    autoRoute: true, component: MyCredentialsPage,
  },
  {
    id: 'census-sync', label: 'Census Sync',
    desc: 'Drop your EMR census export and the whole HOPE roster reconciles in one pass — patients created, updated and discharged — cascading instantly into deadlines and HOPE. The anchor of the hands-free overnight flow.',
    icon: CalendarClock, path: '/app/census-sync',
    category: 'operations', isNew: true, color: 'bg-teal-700',
    tags: ['census', 'emr', 'sync', 'roster', 'import', 'admissions', 'discharge', 'automation', 'overnight'],
    roles: ['admin', 'super_admin', 'compliance', 'clinical_manager'],
    autoRoute: true, component: CensusSyncPage,
  },
  {
    id: 'care-teams', label: 'Care Teams',
    desc: 'Who looks after each patient: RN case manager, LPN, social worker, chaplain and aide. Filled in from your EMR census every import, with manager overrides that stay until removed. Drives each clinician\'s My Day.',
    icon: Users, path: '/app/care-teams',
    category: 'operations', isNew: true, color: 'bg-teal-700',
    tags: ['care team', 'assignment', 'case manager', 'nurse', 'social worker', 'chaplain', 'aide', 'census', 'my day'],
    roles: ['admin', 'super_admin', 'compliance', 'clinical_manager'],
    autoRoute: true, component: CareTeamsPage,
  },
  {
    id: 'deadline-autocomplete', label: 'Deadline Auto-Complete',
    desc: 'Drop an EMR visit/event export and each logged encounter auto-closes its matching CoP deadline — NOE filed, F2F, IDG, aide supervisory visit, recert/CTI. A filed NOE instantly clears the red alert. The partner to Census Sync.',
    icon: CalendarClock, path: '/app/deadline-autocomplete',
    category: 'operations', isNew: true, color: 'bg-teal-700',
    tags: ['deadline', 'auto', 'complete', 'visits', 'events', 'noe', 'f2f', 'idg', 'aide', 'emr', 'automation', 'overnight'],
    roles: ['admin', 'super_admin', 'compliance', 'clinical_manager'],
    autoRoute: true, component: DeadlineAutoCompletePage,
  },
  {
    id: 'daily-digest', label: 'Daily Digest',
    desc: 'A 9am email briefing to your leadership — overdue deadlines, at-risk NOEs, expiring credentials and what last night\'s sync did. Turns silent automation into one glanceable morning email.',
    icon: CalendarClock, path: '/app/daily-digest',
    category: 'operations', isNew: true, color: 'bg-teal-600',
    tags: ['digest', 'email', 'daily', 'briefing', 'morning', 'alerts', 'notifications', 'summary', 'automation'],
    roles: ['admin', 'super_admin', 'compliance', 'clinical_manager'],
    autoRoute: true, component: DailyDigestPage,
  },
  // 🛡️ Audit Defense
  {
    id: 'adr-builder', label: 'ADR Response Builder',
    desc: 'MD-grade cover letter from your visit notes with strict anti-fabrication. Auto-fills from MAC ADR letter. Exports PDF + Word.',
    icon: FileSignature, path: '/app/adr-response-builder',
    category: 'defense', isNew: true, color: 'bg-purple-600', tags: ['adr', 'appeal', 'cover letter', 'cgs'],
  },
  {
    id: 'audit-defense', label: 'Defense Generator',
    desc: 'Generate appeal packages with LCD evidence + visit-note crosswalk in one click.',
    icon: FileCheck, path: '/app/audit-defense',
    category: 'defense', color: 'bg-red-600', tags: ['appeal', 'defense'],
  },
  {
    id: 'tpe-audit', label: 'TPE Audit Simulator',
    desc: '3-layer TPE engine + CGS dual-engine concurrent execution. Score eligibility before billing.',
    icon: ShieldCheck, path: '/app/tpe-audit',
    category: 'defense', color: 'bg-blue-600', tags: ['tpe', 'cgs', 'audit'],
  },
  {
    id: 'visit-audit', label: 'CGS Audit Protection',
    desc: 'Pre-billing CGS audit with discipline-aware scoring (RN, MSW, Chaplain, HHA).',
    icon: FileSearch, path: '/app/visit-audit',
    category: 'defense', color: 'bg-cyan-600', tags: ['cgs', 'visit', 'audit'],
  },
  {
    id: 'cgs-history', label: 'Defense History',
    desc: 'Every appeal you\'ve generated, with prior history tracking for multi-level cases.',
    icon: History, path: '/app/cgs-audit-history',
    category: 'defense', color: 'bg-slate-600', tags: ['history', 'appeals'],
  },
  {
    id: 'advanced-compliance', label: 'Advanced Compliance Tools',
    desc: 'LCD crosswalk, discrepancy scanner, addendum generator (Medicare-compliant).',
    icon: Zap, path: '/app/advanced-compliance',
    category: 'defense', color: 'bg-amber-600', tags: ['lcd', 'addendum'],
  },

  // 📊 Analytics & Insights
  {
    id: 'ssvi-dashboard', label: 'SSVI Analytics',
    desc: 'CMS Service & Spending Variation Index — projected 0–16 score, risk band, and the 9 drivers, from your claims + census.',
    icon: Activity, path: '/app/ssvi',
    category: 'analytics', isNew: true, color: 'bg-indigo-700',
    tags: ['ssvi', 'cms', 'variation', 'spending', 'risk', 'live discharge'],
    roles: ['admin', 'super_admin', 'compliance', 'clinical_manager'],
    autoRoute: true, component: SSVIDashboard,
  },
  {
    id: 'ssvi-benchmarking', label: 'SSVI Benchmarking',
    desc: 'Compare each SSVI measure against uploaded peer benchmarks, project your trajectory, and auto-refresh nightly.',
    icon: Activity, path: '/app/ssvi/benchmarking',
    category: 'analytics', isNew: true, color: 'bg-indigo-700',
    tags: ['ssvi', 'benchmark', 'peer', 'projection', 'trend', 'forecast'],
    roles: ['admin', 'super_admin', 'compliance', 'clinical_manager'],
    autoRoute: true, component: SSVIBenchmarkingPage,
  },
  {
    id: 'tpe-tracker', label: 'TPE Remediation Tracker',
    desc: 'Track Targeted Probe & Educate rounds: error findings, remediation tasks, education completed, and re-review outcomes.',
    icon: ShieldCheck, path: '/app/tpe-tracker',
    category: 'defense', isNew: true, color: 'bg-blue-700',
    tags: ['tpe', 'probe', 'educate', 'remediation', 'mac', 'error rate', 'audit'],
    roles: ['admin', 'super_admin', 'compliance', 'clinical_manager'],
    autoRoute: true, component: TPETrackerPage,
  },
  {
    id: 'defense-exec-report', label: 'Defense Executive Review',
    desc: 'Executive report of favorable audit-defense outcomes — dollars protected, win rate, and wins by medical director, with AI insights and a board-ready PDF.',
    icon: ShieldCheck, path: '/app/defense-executive-review',
    category: 'defense', isNew: true, color: 'bg-blue-800',
    tags: ['defense', 'appeal', 'favorable', 'executive', 'cost savings', 'roi', 'medical director', 'board'],
    roles: ['admin', 'super_admin', 'compliance', 'clinical_manager'],
    autoRoute: true, component: DefenseExecReportPage,
  },
  {
    id: 'cahps-optimizer', label: 'CAHPS Optimizer',
    desc: 'Surface Hospice CAHPS survey scores vs national benchmarks with AI-suggested, prioritized improvement actions.',
    icon: BarChart3, path: '/app/cahps-optimizer',
    category: 'analytics', isNew: true, color: 'bg-emerald-700',
    tags: ['cahps', 'survey', 'quality', 'star', 'family experience', 'ai'],
    roles: ['admin', 'super_admin', 'compliance', 'clinical_manager'],
    autoRoute: true, component: CAHPSOptimizerPage,
  },
  {
    id: 'network-oversight', label: 'Network Oversight',
    desc: 'Parent-org rollup of SSVI risk, HOPE compliance & survey-readiness across every member agency — with peer benchmarking, quarterly trends, and a board-ready PDF.',
    icon: Network, path: '/app/network',
    category: 'analytics', isNew: true, color: 'bg-violet-700',
    tags: ['network', 'multi-site', 'chain', 'benchmark', 'oversight', 'chap', 'parent org'],
    roles: ['super_admin', 'network_admin'],
    autoRoute: true, component: NetworkOversight,
  },
  {
    id: 'network-modules', label: 'Modules & Pricing',
    desc: 'Price the network by ADC band and modules, and control which capabilities each agency can access with per-tenant module gating.',
    icon: Network, path: '/app/network/modules',
    category: 'analytics', isNew: true, color: 'bg-indigo-700',
    tags: ['pricing', 'modules', 'gating', 'calculator', 'adc', 'billing', 'network'],
    roles: ['super_admin'],
    autoRoute: true, component: NetworkModulesPage,
  },
  {
    id: 'risk-cockpit', label: 'Predictive Risk Cockpit',
    desc: '0-100 audit-denial risk per patient with top drivers + 5 actionable steps.',
    icon: Target, path: '/app/risk-cockpit',
    category: 'analytics', isNew: true, color: 'bg-orange-600', tags: ['risk', 'predictive', 'census'],
  },
  {
    id: 'roi', label: 'ROI / Money Saved',
    desc: 'Live "$X saved this quarter" dashboard with breakdown + downloadable PDF report.',
    icon: DollarSign, path: '/app/roi',
    category: 'analytics', isNew: true, color: 'bg-emerald-600', tags: ['roi', 'money', 'report'],
  },
  {
    id: 'executive', label: 'Analytics Dashboard',
    desc: 'Executive insights: audit pass rate, denial trends, agency benchmarks.',
    icon: BarChart3, path: '/app/executive',
    category: 'analytics', color: 'bg-indigo-600', tags: ['analytics', 'kpi'],
  },
  {
    id: 'regulatory', label: 'Regulatory Updates',
    desc: 'Live CMS / MAC / LCD transmittal updates filtered to what affects your agency.',
    icon: Newspaper, path: '/app/regulatory-feed',
    category: 'analytics', color: 'bg-blue-700', tags: ['cms', 'lcd', 'updates'],
  },

  // 🤖 AI Tools
  {
    id: 'coding-assistant', label: 'AI Coding Assistant',
    desc: 'ICD-10, HOPE, OASIS-E1, HCPCS suggestions with verbatim evidence + OCR file upload.',
    icon: FileCode, path: '/app/coding-assistant',
    category: 'ai', isNew: true, color: 'bg-blue-600', tags: ['icd10', 'hope', 'oasis', 'hcpcs', 'coding'],
  },
  {
    id: 'scribe', label: 'AI Scribe',
    desc: 'Voice-to-note with 10 medical accents and Medicare-compliant rewrite.',
    icon: Mic, path: '/app/scribe',
    category: 'ai', color: 'bg-violet-600', tags: ['scribe', 'voice', 'whisper'],
  },
  {
    id: 'voice-note', label: 'One-Click Voice Note',
    desc: 'Tap record. Get a compliant SOAP note with LCD analysis in under 60 seconds.',
    icon: Mic, path: '/app/voice-note',
    category: 'ai', isNew: true, color: 'bg-emerald-600', tags: ['scribe', 'voice', 'quick', 'bedside', 'lcd'],
  },
  {
    id: 'idg-auto-prep', label: 'IDG Auto-Prep',
    desc: 'Every 14 days, roll up every scribe note into a Medicare-compliant IDT packet in one click.',
    icon: Users, path: '/app/idg-auto-prep',
    category: 'workflow', isNew: true, color: 'bg-emerald-700', tags: ['idg', 'idt', 'summary', 'compliance', 'bi-weekly'],
  },
  {
    id: 'compliance-ai', label: 'Compliance Coach',
    desc: 'Real-time compliance Q&A with cited Medicare references.',
    icon: Brain, path: '/app/compliance',
    category: 'ai', color: 'bg-pink-600', tags: ['ai', 'qa', 'coach'],
  },

  // 🔒 Compliance & Security
  {
    id: 'verification', label: 'Verification Sequence',
    desc: 'Mandatory clinical sign-off gate — verify patient identity, meds & doses, symptoms, diagnoses, orders and degree of certainty before any AI note is used. Signed, tamper-evident receipt.',
    icon: ShieldCheck, path: '/app/verification',
    category: 'compliance', isNew: true, color: 'bg-emerald-700',
    tags: ['verification', 'sign-off', 'attestation', 'patient identity', 'medications', 'doses', 'diagnoses', 'orders', 'safety'],
    autoRoute: true, component: VerificationSequencePage,
  },
  {
    id: 'survey-readiness', label: 'Survey Readiness',
    desc: 'Mock Medicare CoP surveys — score deficiencies and auto-draft surveyor-ready Plans of Correction.',
    icon: ClipboardCheck, path: '/app/survey-readiness',
    category: 'compliance', isNew: true, color: 'bg-emerald-700',
    tags: ['survey', 'chap', 'cop', 'accreditation', 'poc', 'readiness'],
    roles: ['admin', 'super_admin', 'compliance', 'clinical_manager'],
    autoRoute: true, component: SurveyReadinessPage,
  },
  {
    id: 'hipaa-proof', label: 'HIPAA Proof Center',
    desc: 'Live encryption demo, Safe Harbor de-id, FHIR push receipts, signed attestation PDF.',
    icon: FileSignature, path: '/app/hipaa-proof',
    category: 'compliance', isNew: true, color: 'bg-emerald-700', tags: ['hipaa', 'encryption', 'fhir'],
  },
  {
    id: 'hipaa-center', label: 'HIPAA Center',
    desc: 'Encryption status, BAA tracking, security policy hub.',
    icon: ShieldCheck, path: '/app/hipaa-compliance',
    category: 'compliance', color: 'bg-emerald-600', tags: ['hipaa'],
  },
  {
    id: 'phi-audit', label: 'PHI Audit Trail',
    desc: 'Tamper-evident log of every PHI access — 6-year retention.',
    icon: Shield, path: '/app/phi-audit',
    category: 'compliance', color: 'bg-slate-700', tags: ['phi', 'audit', 'log'],
  },
  {
    id: 'compliance-alerts', label: 'Compliance Alerts',
    desc: 'Real-time notifications for ADRs, recerts, deadlines, F2F windows.',
    icon: Bell, path: '/app/compliance-alerts',
    category: 'compliance', color: 'bg-amber-600', tags: ['alerts', 'notifications'],
  },

  // 📱 Mobile / Field
  {
    id: 'field-mode', label: 'Field Mode (Mobile)',
    desc: 'Offline-capable visit-note capture for field nurses with voice dictation.',
    icon: Smartphone, path: '/app/field-mode',
    category: 'mobile', isNew: true, color: 'bg-teal-600', tags: ['mobile', 'offline', 'pwa', 'field'],
  },

  // 🏥 Patient & Operations
  {
    id: 'dashboard', label: 'Dashboard',
    desc: 'Daily snapshot of audits, ROI, alerts, and at-risk patients.',
    icon: Activity, path: '/app/dashboard',
    category: 'operations', color: 'bg-slate-600', tags: ['home', 'dashboard'],
  },
  {
    id: 'patients', label: 'Patient Tracker',
    desc: 'Census view with risk scoring and visit-note timeline.',
    icon: Users, path: '/app/patient-tracker',
    category: 'operations', color: 'bg-blue-600', tags: ['patients', 'census', 'tracker'],
  },
  {
    id: 'documents', label: 'Templates & Documents',
    desc: 'Centralized clinical template & document library with tags + search.',
    icon: ClipboardList, path: '/app/templates',
    category: 'operations', color: 'bg-cyan-600', tags: ['documents', 'files', 'templates'],
  },
  {
    id: 'compliance-calendar', label: 'Compliance Calendar',
    desc: 'Recerts, ADR deadlines, IDG meetings on one calendar.',
    icon: Calendar, path: '/app/compliance-calendar',
    category: 'operations', color: 'bg-teal-600', tags: ['calendar', 'deadlines'],
  },

  // ⚙️ Admin
  {
    id: 'command-center', label: 'Command Center',
    desc: 'Real-time operations overview with system health and live activity.',
    icon: Cpu, path: '/app/command-center',
    category: 'admin', color: 'bg-slate-800', tags: ['command', 'ops'],
  },
  {
    id: 'org-mgmt', label: 'Organization Settings',
    desc: 'Tenant config, branding, team access.',
    icon: Building2, path: '/app/organization',
    category: 'admin', roles: ['admin', 'super_admin'], color: 'bg-purple-700', tags: ['org', 'settings'],
  },
  {
    id: 'integrations', label: 'Integrations',
    desc: 'EMR, billing, fax, and 3rd-party connectors.',
    icon: Globe, path: '/app/emr-integration',
    category: 'admin', color: 'bg-sky-600', tags: ['emr', 'integrations'],
  },
];

// Features that own their route via the registry (single source of truth).
export const AUTO_ROUTED_FEATURES = FEATURE_REGISTRY.filter(
  (f) => f.autoRoute && f.component
).map((f) => ({
  id: f.id,
  routePath: f.path.replace(/^\/app\//, ''),
  component: f.component,
  roles: f.roles,
}));
